/* eslint-disable @typescript-eslint/default-param-last */
import type {
    FlattenedMigratedStyleItem,
    ParsedStyleItem,
    PostStyleMigrationCallback,
    Property,
    StyleItemWithStyleProperties
} from './types'

const handleSelectorContainState = (selector: string, prop: string): string => {
    const parts = selector.split('-')
    const state = parts[1]
    return `${state}-${prop}`
}

const createSelectorKey = (selector: string, prop: string, removePrefix: boolean): string => {
    if (removePrefix) {
        return selector.includes('-') ? handleSelectorContainState(selector, prop) : prop
    }
    return `${selector}-${prop}`
}

const flattenProperties = (
    selector: string,
    properties: Property[] = [],
    removePrefix: boolean
): FlattenedMigratedStyleItem => {
    return properties.reduce((acc, property) => {
        const key = property.absolutePropName
            ? property.absolutePropName
            : createSelectorKey(selector, property.prop, removePrefix)
        return {
            ...acc,
            [key]: property.value
        }
    }, {} as FlattenedMigratedStyleItem)
}

const flattenPropertiesOverride = (
    selector: string,
    propertiesOverride: ParsedStyleItem['parsedStyle'][0]['propertiesOverride'] = {},
    removePrefix: boolean
): FlattenedMigratedStyleItem => {
    return Object.entries(propertiesOverride).reduce((flattenedPropertiesOverride, [prop, override]) => {
        const key = createSelectorKey(selector, prop, removePrefix)
        return {
            ...flattenedPropertiesOverride,
            [key]: override
        }
    }, {} as FlattenedMigratedStyleItem)
}

const flattenPropertiesSource = (
    selector: string,
    propertiesSource: ParsedStyleItem['parsedStyle'][0]['propertiesSource'] = {},
    removePrefix: boolean
): FlattenedMigratedStyleItem => {
    return Object.entries(propertiesSource).reduce((flattenedPropertiesSource, [prop, source]) => {
        const key = createSelectorKey(selector, prop, removePrefix)
        return {
            ...flattenedPropertiesSource,
            [prop.includes(selector) ? prop : key]: source
        }
    }, {} as FlattenedMigratedStyleItem)
}

const flattenParsedStyle = (
    parsedStyle: ParsedStyleItem['parsedStyle']
): Pick<
    FlattenedMigratedStyleItem,
    'properties' | 'mobileProperties' | 'propertiesOverride' | 'mobilePropertiesOverride' | 'propertiesSource'
> => {
    return Object.entries(parsedStyle).reduce(
        (
            flattenedParsedStyle,
            [
                selector,
                {
                    mobileProperties,
                    properties,
                    mobilePropertiesOverride,
                    propertiesOverride,
                    propertiesSource,
                    removePrefix
                }
            ]
        ) => {
            return {
                properties: {
                    ...flattenedParsedStyle.properties,
                    ...flattenProperties(selector, properties, !!removePrefix)
                },
                mobileProperties: {
                    ...flattenedParsedStyle.mobileProperties,
                    ...flattenProperties(selector, mobileProperties, !!removePrefix)
                },
                propertiesOverride: {
                    ...flattenedParsedStyle.propertiesOverride,
                    ...flattenPropertiesOverride(selector, propertiesOverride, !!removePrefix)
                },
                mobilePropertiesOverride: {
                    ...flattenedParsedStyle.mobilePropertiesOverride,
                    ...flattenPropertiesOverride(selector, mobilePropertiesOverride, !!removePrefix)
                },
                propertiesSource: {
                    ...flattenedParsedStyle.propertiesSource,
                    ...flattenPropertiesSource(selector, propertiesSource, !!removePrefix)
                }
            }
        },
        {
            properties: {},
            mobileProperties: {},
            propertiesOverride: {},
            mobilePropertiesOverride: {},
            propertiesSource: {}
        }
    )
}

const getFlattenedMigratedStyle = (migratedStyleItem: ParsedStyleItem): FlattenedMigratedStyleItem => {
    return {
        ...migratedStyleItem,
        ...flattenParsedStyle(migratedStyleItem.parsedStyle)
    }
}

const migratedStyleToStyleProperties = (
    migratedStyleItem: ParsedStyleItem,
    options: {
        isMobile?: boolean
        postStyleMigrationCallback?: PostStyleMigrationCallback
    } = {}
): StyleItemWithStyleProperties['style'] => {
    const {isMobile = false, postStyleMigrationCallback} = options
    const flattenedStyle = getFlattenedMigratedStyle(migratedStyleItem)
    const composedStyle: StyleItemWithStyleProperties['style'] = {
        properties: isMobile ? flattenedStyle.mobileProperties : flattenedStyle.properties,
        propertiesOverride: isMobile ? flattenedStyle.mobilePropertiesOverride : flattenedStyle.propertiesOverride,
        groups: {},
        propertiesSource: flattenedStyle.propertiesSource
    }

    if (postStyleMigrationCallback) {
        return postStyleMigrationCallback(composedStyle, migratedStyleItem.style)
    }

    return composedStyle
}

export {migratedStyleToStyleProperties}
